import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import TextToSpeechPlayer from './TextToSpeechPlayer';
import searchPaletteData from '../data/components/search-palette.json';
import * as S from '../styles/components/SearchPalette.styles';

interface SearchPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchPalette: React.FC<SearchPaletteProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showTextToSpeech, setShowTextToSpeech] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const { search } = useContent();
  
  // Read language from localStorage (managed by Layout)
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Listen for language changes from Layout
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);
  
  const t = searchPaletteData[language];
  
  // Track if user has used Tab to navigate (not mouse click)
  const hasReceivedTabFocus = useRef(false);
  const isMouseClick = useRef(false);
  const paletteContainerRef = useRef<HTMLDivElement>(null);

  // Move these functions outside useCallback to avoid circular dependencies
  const escapeRegex = (str: string): string => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightImportantParts = (text: string): string => {
    // Highlight phone numbers (+43...)
    text = text.replace(/(\+43\s?\d{3,4}\s?\d{6,7})/g, '<mark>$1</mark>');
    
    // Highlight email addresses
    text = text.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<mark>$1</mark>');
    
    // Highlight opening hours patterns
    text = text.replace(/(\d{1,2}:\d{2}\s*bis\s*\d{1,2}:\d{2}\s*Uhr)/gi, '<mark>$1</mark>');
    text = text.replace(/(von\s*\d{1,2}:\d{2}\s*bis\s*\d{1,2}:\d{2})/gi, '<mark>$1</mark>');
    
    // Highlight days of week
    text = text.replace(/(Montag|Dienstag|Mittwoch|Donnerstag|Freitag|Samstag|Sonntag)/gi, '<mark>$1</mark>');
    
    // Highlight "KONTAKT" sections
    text = text.replace(/(KONTAKT.*?:)/gi, '<mark>$1</mark>');
    
    return text;
  };

  const highlightTextInString = useCallback((text: string, query: string): string => {
    if (!query) return highlightImportantParts(text);
    
    // First highlight important parts
    let result = highlightImportantParts(text);
    
    // Support multiple keywords separated by |
    const keywords = query.split('|').map(k => k.trim()).filter(k => k);
    
    // Highlight each keyword
    keywords.forEach(keyword => {
      const regex = new RegExp(`(${escapeRegex(keyword)})`, 'gi');
      if (regex.test(result)) {
        // Check if already inside a mark tag
        const lines = result.split('\n');
        result = lines.map(line => {
          if (line.includes('<mark>')) return line;
          return line.replace(regex, '<mark>$1</mark>');
        }).join('\n');
      }
    });
    
    return result;
  }, []);

  const handleOpenBackground = React.useCallback((result: any) => {
    const searchParams = new URLSearchParams();
    // Use highlightKeywords from AI if available, otherwise use query for normal search
    const highlightTerm = result.highlightKeywords?.length > 0
      ? result.highlightKeywords.join('|')
      : query;
    searchParams.set('highlight', highlightTerm);
    
    // Add section parameter if available to scroll to specific section
    if (result.sectionId) {
      searchParams.set('section', result.sectionId);
    }
    
    const path = `${result.path}?${searchParams.toString()}`;
    navigate(path);
    // Keep search open
  }, [navigate, query]);

  const handleOpenAndClose = React.useCallback((result: any) => {
    const searchParams = new URLSearchParams();
    // Use highlightKeywords from AI if available, otherwise use query for normal search
    const highlightTerm = result.highlightKeywords?.length > 0
      ? result.highlightKeywords.join('|')
      : query;
    searchParams.set('highlight', highlightTerm);
    
    // Add section parameter if available to scroll to specific section
    if (result.sectionId) {
      searchParams.set('section', result.sectionId);
    }
    
    const path = `${result.path}?${searchParams.toString()}`;
    navigate(path);
    onClose();
  }, [navigate, onClose, query]);


  // Helper function to close search and stop text-to-speech
  const closeSearchAndStopSpeech = React.useCallback(() => {
    if (showTextToSpeech) {
      window.speechSynthesis.cancel();
      setShowTextToSpeech(false);
    }
    onClose();
  }, [showTextToSpeech, onClose]);

  // Search logic with sorting
  useEffect(() => {
    if (query.trim() === '') {
      setResults([]);
      setActiveIndex(0);
      return;
    }

    // Perform normal search on typing
    const searchResults = search(query);
    const formattedResults = searchResults.map(item => ({
      ...item,
      snippet: highlightTextInString(item.snippet, query)
    }));
    
    // Sort results: first current page results (top to bottom), then others alphabetically
    const currentPath = location.pathname;
    const currentPageResults: any[] = [];
    const otherResults: any[] = [];
    
    // Define order for Home page sections (from top to bottom)
    const homeSectionOrder: Record<string, number> = {
      'home': 0,
      'intro': 1,
      'yoga-outdoor': 2,
      'yoga-online': 3,
      'testimonials': 4,
      'newsletter': 5,
      'contact': 6
    };
    
    formattedResults.forEach(item => {
      // Check if result is from current page
      const isCurrentPage = item.path === currentPath || 
                           (currentPath === '/' && item.path === '/') ||
                           (currentPath === '/impressum' && item.path === '/impressum') ||
                           (currentPath === '/dsgvo' && item.path === '/dsgvo');
      
      if (isCurrentPage) {
        currentPageResults.push(item);
      } else {
        otherResults.push(item);
      }
    });
    
    // Sort current page results by section order (if Home page) or by title
    if (currentPath === '/') {
      currentPageResults.sort((a, b) => {
        const orderA = a.sectionId ? (homeSectionOrder[a.sectionId] ?? 999) : 999;
        const orderB = b.sectionId ? (homeSectionOrder[b.sectionId] ?? 999) : 999;
        return orderA - orderB;
      });
    } else {
      // For other pages, sort by title alphabetically
      currentPageResults.sort((a, b) => a.title.localeCompare(b.title));
    }
    
    // Sort other results alphabetically by page title, then by result title
    otherResults.sort((a, b) => {
      const pageCompare = a.page.localeCompare(b.page);
      if (pageCompare !== 0) return pageCompare;
      return a.title.localeCompare(b.title);
    });
    
    // Combine: current page first, then others
    setResults([...currentPageResults, ...otherResults]);
    setActiveIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, location.pathname]); // Add location.pathname to dependencies

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  // Reset Tab-Focus-Flag wenn Palette geschlossen wird
  useEffect(() => {
    if (!isOpen) {
      hasReceivedTabFocus.current = false;
      // Remove focus from any element inside the palette when closed
      if (paletteContainerRef.current && document.activeElement && paletteContainerRef.current.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
    }
  }, [isOpen]);

  // Focus Trap: Get all focusable elements within the palette
  const getFocusableElements = useCallback(() => {
    if (!paletteContainerRef.current) return [];
    
    const focusableSelectors = [
      'input:not([disabled]):not([type="hidden"])',
      'button:not([disabled])',
      'a[href]',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    return Array.from(
      paletteContainerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter(el => {
      // Filter out elements that are not visible
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
  }, []);

  // Keyboard navigation + Focus Trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || !paletteContainerRef.current) return;

      // Wenn Text-to-Speech Player aktiv ist, nicht eingreifen (Player hat Priorität)
      if (showTextToSpeech && e.key === 'Tab') {
        const playerContainer = document.querySelector('[data-text-to-speech-player]');
        const activeElement = document.activeElement as HTMLElement;
        // Wenn Player aktiv ist, lasse den Player-Focus Trap übernehmen
        if (playerContainer) {
          if (playerContainer.contains(activeElement) || showTextToSpeech) {
            // Player-Focus Trap übernimmt - nicht eingreifen
            return;
          }
        }
      }

      // Focus Trap: Keep Tab navigation within the palette (nur wenn Player nicht aktiv)
      if (e.key === 'Tab' && !showTextToSpeech) {
        const focusableElements = getFocusableElements();
        
        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement;

        // Check if active element is within the palette
        const isWithinPalette = paletteContainerRef.current.contains(activeElement);

        // If focus is outside palette, focus first element
        if (!isWithinPalette) {
          e.preventDefault();
          firstElement.focus();
          hasReceivedTabFocus.current = true;
          return;
        }

        // Handle Tab navigation within palette
        if (e.shiftKey) {
          // Shift+Tab: If on first element, wrap to last
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: If on last element, wrap to first
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
        
        // Mark that Tab has been used
        hasReceivedTabFocus.current = true;
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex(prev => Math.min(prev + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[activeIndex]) {
            // Default: open in background
            handleOpenBackground(results[activeIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          // Remove focus before closing
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, activeIndex, handleOpenBackground, onClose, showTextToSpeech, getFocusableElements]);

  // Stop text-to-speech when search closes
  useEffect(() => {
    if (!isOpen) {
      if (showTextToSpeech) {
        window.speechSynthesis.cancel();
        setShowTextToSpeech(false);
      }
    }
  }, [isOpen, showTextToSpeech]);

  if (!isOpen) return null;

  return (
    <>
    <AnimatePresence>
      <S.Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <S.PaletteContainer
          ref={paletteContainerRef}
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <S.SearchHeader>
            <S.SearchIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </S.SearchIcon>
            <S.SearchInput
              ref={inputRef}
              type="text"
              placeholder={t.placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Enter key handling removed - no AI search
                // Mark keyboard navigation
                if (e.key === 'Tab') {
                  isMouseClick.current = false;
                  hasReceivedTabFocus.current = true;
                  e.currentTarget.classList.remove('mouse-focus');
                }
              }}
              onMouseDown={(e) => {
                // Mark that this is a mouse click, not keyboard navigation
                if (e.button === 0) { // Left mouse button
                  isMouseClick.current = true;
                  hasReceivedTabFocus.current = false;
                  e.currentTarget.classList.add('mouse-focus');
                }
              }}
              onFocus={(e) => {
                // Remove focus ring if it was a mouse click
                if (isMouseClick.current) {
                  e.currentTarget.classList.add('mouse-focus');
                } else {
                  e.currentTarget.classList.remove('mouse-focus');
                }
              }}
              onBlur={(e) => {
                // Reset flag and class when losing focus
                isMouseClick.current = false;
                e.currentTarget.classList.remove('mouse-focus');
              }}
            />
            <S.KeyboardHint>
              <kbd>ESC</kbd>
            </S.KeyboardHint>
            <S.CloseButton colors={colors} onClick={closeSearchAndStopSpeech} aria-label={t.closeButton}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </S.CloseButton>
          </S.SearchHeader>

          <div style={{ padding: '12px 20px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 'clamp(0.9rem, 2vw, 0.85rem)', color: '#6b7280', flex: 1, minWidth: '200px' }}>
              {t.textToSpeech.description}
            </div>
            <button
              onClick={() => {
                setShowTextToSpeech(true);
              }}
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 0.9rem)',
                padding: '0.6rem 1.2rem',
                flexShrink: 0,
                background: colors.primary,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.primary;
              }}
            >
              {t.textToSpeech.button}
            </button>
          </div>

          <S.ResultsList ref={resultsListRef}>
            {query.trim() === '' ? (
              <S.EmptyState>
                {t.emptyState.typing}
              </S.EmptyState>
            ) : results.length === 0 ? (
              <S.EmptyState>
                {t.emptyState.noResults.replace('{query}', query)}
              </S.EmptyState>
            ) : (
              results.map((result, index) => (
                <S.ResultItem
                  key={result.id}
                  colors={colors}
                  isActive={index === activeIndex}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <S.ResultTitle>
                    {result.page} - {result.title}
                  </S.ResultTitle>
                  <S.ResultPath colors={colors}>{result.path}</S.ResultPath>
                  <S.ResultPreview 
                    colors={colors}
                    dangerouslySetInnerHTML={{ __html: result.snippet }}
                  />
                  <S.ResultActions onClick={(e) => e.stopPropagation()}>
                    <S.ActionButton 
                      colors={colors}
                      variant="primary"
                      onClick={() => handleOpenAndClose(result)}
                    >
                      {t.actions.open}
                    </S.ActionButton>
                    <S.ActionButton 
                      colors={colors}
                      variant="secondary"
                      onClick={() => handleOpenBackground(result)}
                    >
                      {t.actions.openBackground}
                    </S.ActionButton>
                  </S.ResultActions>
                </S.ResultItem>
              ))
            )}
          </S.ResultsList>

          {results.length > 0 && (
            <S.NavigationHint>
              <span><kbd>↑</kbd><kbd>↓</kbd> {t.navigation.navigate}</span>
              <span><kbd>↵</kbd> {t.navigation.open}</span>
              <span><kbd>ESC</kbd> {t.navigation.close}</span>
            </S.NavigationHint>
          )}
        </S.PaletteContainer>
      </S.Overlay>
    </AnimatePresence>
    
    {/* Text-to-Speech Player für Suchergebnisse */}
    <TextToSpeechPlayer 
      isActive={showTextToSpeech} 
      onClose={() => setShowTextToSpeech(false)}
      targetElement={resultsListRef.current}
    />
  </>
  );
};

export default SearchPalette;

